import React, { useState } from "react";
import { client, setAuthToken } from "../api";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const FEATURES = [
  { Icon: ChatBubbleOutlineRoundedIcon, title: "Product-page Q&A", desc: "An inline assistant that answers only about the product on the page." },
  { Icon: SearchRoundedIcon, title: "Suggested questions", desc: "Curated question chips per product category, GC-style." },
  { Icon: InsightsRoundedIcon, title: "Grounded answers", desc: "Answers come from your indexed catalog and per-product notes." },
];

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await client.post("/api/auth/login", { email, password });
      setAuthToken(data.access_token);
      onLogin(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join("; ") : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Brand panel */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1.05,
          flexDirection: "column",
          justifyContent: "center",
          gap: 4,
          p: { md: 6, lg: 9 },
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          background: "linear-gradient(150deg,#0d9488 0%,#0f766e 55%,#134e4a 100%)",
        }}
      >
        {/* decorative glow */}
        <Box sx={{ position: "absolute", top: -120, right: -120, width: 360, height: 360, borderRadius: "50%", background: "rgba(255,255,255,0.10)" }} />
        <Box sx={{ position: "absolute", bottom: -140, left: -100, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />

        <Stack direction="row" alignItems="center" gap={1.5} sx={{ position: "relative" }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AutoAwesomeRoundedIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>Product Assistant Studio</Typography>
        </Stack>

        <Box sx={{ position: "relative" }}>
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1, mb: 1.5 }}>
            AI answers on every product page
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 17, maxWidth: 460 }}>
            Configure suggested questions, per-product notes, and the embed — the assistant answers
            only about the product it sits on.
          </Typography>
        </Box>

        <Stack spacing={2.5} sx={{ position: "relative" }}>
          {FEATURES.map(({ Icon, title, desc }) => (
            <Stack key={title} direction="row" gap={2} alignItems="flex-start">
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}>{desc}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Form panel */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 2.5, md: 6 } }}>
        <Card sx={{ width: "100%", maxWidth: 420 }}>
          <CardContent sx={{ p: { xs: 3, md: 4.5 } }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Welcome back</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sign in to manage your product-page assistants.
            </Typography>
            <Box component="form" onSubmit={submit}>
              <Stack spacing={2.25}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  autoFocus
                  autoComplete="username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button variant="contained" type="submit" size="large" disabled={loading} sx={{ py: 1.25, fontSize: 16 }}>
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginPage;
